/**
 * api/utils.ts — utilidades de mapping/format pra UI.
 */

import type { RequestStatus as ApiRequestStatus } from './types';
import type { RequestStatus as UiRequestStatus } from '@/components/ui/StatusBadge';

const STATUS_MAP: Record<ApiRequestStatus, UiRequestStatus> = {
  PendingApproval: 'pending',
  Approved: 'approved',
  Denied: 'denied',
  Granted: 'active',
  Expired: 'expired',
  Revoked: 'revoked',
};

export function mapApiStatusToUi(status: ApiRequestStatus): UiRequestStatus {
  return STATUS_MAP[status];
}

export function formatIsoToBR(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const date = new Date(iso);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const date = new Date(iso);
    const diffMs = date.getTime() - Date.now();
    const diffMin = Math.round(diffMs / 60000);

    if (Math.abs(diffMin) < 1) return 'agora';
    if (diffMin > 0) {
      if (diffMin < 60) return `em ${diffMin}min`;
      const h = Math.round(diffMin / 60);
      if (h < 24) return `em ${h}h`;
      const d = Math.round(h / 24);
      return `em ${d}d`;
    }
    const past = -diffMin;
    if (past < 60) return `há ${past}min`;
    const h = Math.round(past / 60);
    if (h < 24) return `há ${h}h`;
    const d = Math.round(h / 24);
    return `há ${d}d`;
  } catch {
    return iso;
  }
}

const IDC_GROUP_TO_ROLE: Record<string, string[]> = {
  'Aura-Engineers': ['requester'],
  'Aura-TEAMAdmins': ['admin', 'approver'],
  'Aura-TEAMAuditors': ['auditor'],
};

/** Map de IdC groups → roles internos pro RBAC do frontend. */
export function rolesFromGroups(groups: string[] | undefined): Set<string> {
  const roles = new Set<string>();
  for (const g of groups ?? []) {
    for (const r of IDC_GROUP_TO_ROLE[g] ?? []) {
      roles.add(r);
    }
  }
  return roles;
}

export function isApprover(groups: string[] | undefined): boolean {
  return rolesFromGroups(groups).has('approver');
}

export function isAuditor(groups: string[] | undefined): boolean {
  return rolesFromGroups(groups).has('auditor') || rolesFromGroups(groups).has('admin');
}

export function isClientAdmin(groups: string[] | undefined): boolean {
  return rolesFromGroups(groups).has('admin');
}
